<script setup lang="ts">
import { LocationOption, toLocaleDateFormat } from '#imports'

definePageMeta({ isPublic: true })

const { $sanitizeHTML } = useNuxtApp()

const { t, locale } = useI18n({
  useScope: 'local',
})

usePageTitle(computed(() => t('pageTitle')))

const route = useRoute()

const { vacancy, relationships, refresh } = await fetchVacancyDeprecated(route.params.id as string)
const organization = computed(() => relationships.value.organization)

const audiences = computed(() => {
  return relationships.value.audiences
})
const categories = computed(() => {
  return relationships.value.categories
})
const activityTypes = computed(() => {
  return relationships.value.activityTypes
})
const address = computed(() => {
  return relationships.value?.address
})

const googleMapsUrl = computed(
  () =>
    `https://www.google.com/maps/search/?api=1&query=${address.value?.latitude},${address.value?.longitude}`,
)

const { datesAreInTheSameMonth, upcomingDates } = useVacancyDates(vacancy.value)

// Dmob had HTML descriptions, instead of markdown
const isHtmlDescription = computed(() => {
  return vacancy.value.fullDescription.includes('<p>')
})

// Determine if the current user has admin rights for this vacancy
const { canManageVacancy, isVacancyCreator }
  = await useVacancyUserContext(vacancy)

const showApplyCard = computed(
  () => vacancy.value.status == VacancyStatus.Published,
)

// For individual P2P vacancies, we don't show the organization section.
// The focus is on the volunteer, instead
const showOrganizationInfo = computed(() => {
  return !vacancy.value.isIndividualP2P
})
</script>

<template>
  <div v-if="vacancy" class="relative">
    <!-- Vacancy nav -->
    <div class="mb-4 flex py-3">
      <OverviewBackLink />
    </div>

    <div v-if="canManageVacancy" class="flex justify-between">
      <VacancyDetailsStatus :vacancy="vacancy" />

      <VacancyDetailsOptionsMenu
        :vacancy="vacancy"
        @refresh="refresh"
      />
    </div>

    <!-- Grid -->
    <div v-if="vacancy" class="grid grid-cols-12 gap-6">
      <!-- Content column -->
      <div
        class="ltablet:col-span-8 col-span-12 flex flex-col gap-y-6 lg:col-span-8"
      >
        <VacancyDetailsContentCard class="space-y-8">
          <div class="flex flex-col gap-4">
            <BaseHeading
              as="h1"
              size="2xl"
              weight="medium"
            >
              {{ vacancy.title }}
            </BaseHeading>

            <!-- Full description -->
            <MarkdownRenderer
              v-if="!isHtmlDescription"
              :content="vacancy.fullDescription"
              class="text-theme-content -mb-4"
            />
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-else v-html="$sanitizeHTML(vacancy.fullDescription)" />
          </div>

          <VacancyDetailsRequirements :vacancy="vacancy" />

          <VacancyDetailsWhen :vacancy="vacancy" />
        </VacancyDetailsContentCard>

        <VacancyDetailsContentCard
          v-if="showOrganizationInfo && organization.description"
          class="mt-4"
        >
          <BaseHeading
            tag="h3"
            size="xl"
            weight="medium"
          >
            {{ t('aboutOrganization', { name: organization.name }) }}
          </BaseHeading>
          <MarkdownRenderer
            :content="organization.description"
            class="dark:text-muted-100 text-muted-600"
          />
        </VacancyDetailsContentCard>

        <VacancyDetailsP2pExplanationCard v-if="vacancy.isIndividualP2P" />

        <!-- Apply form -->
        <!-- TODO: lg:block is not the correct breakpoint. Need to dig a bit deeper -->
        <VacancyDetailsApplyCard
          v-if="showApplyCard"
          :vacancy="vacancy"
          class="hidden lg:block"
        />

        <VacancyMetaInfo v-if="canManageVacancy" :vacancy="vacancy" />
      </div>

      <!-- Sidebar column -->
      <div class="ltablet:col-span-4 col-span-12 lg:col-span-4">
        <div class="flex flex-col gap-6">
          <VacancyDetailsApplyCard v-if="showApplyCard" :vacancy="vacancy" />

          <VacancyDetailsOrganizationCard
            v-if="showOrganizationInfo"
            :organization="organization"
          />

          <VacancyDetailsContactPersonCard :vacancy="vacancy" />

          <VacancyDetailsImportInfoCard :vacancy="vacancy" />

          <SidebarCard
            v-if="upcomingDates && upcomingDates.length > 0"
            :title="t('when')"
          >
            <Datepicker
              v-if="datesAreInTheSameMonth"
              expanded
              multiple-dates
              disabled
              :model-value="upcomingDates"
            />
            <ul v-else class="flex flex-col gap-2">
              <!--TODO Use the new formatted date component here -->
              <li v-for="date in upcomingDates" :key="date">
                <Icon name="material-symbols:calendar-month" />
                {{ toLocaleDateFormat(date, locale, 'dddd, DD MMMM YYYY') }}
              </li>
            </ul>
          </SidebarCard>

          <SidebarCard>
            <div class="space-y-5">
              <div v-if="activityTypes.length">
                <strong>{{ t('activityType') }}</strong>
                <div class="mt-2 flex flex-wrap gap-2">
                  <BaseTag
                    v-for="tag in activityTypes.map((at) => at.name)"
                    :key="tag"
                    class="dark:!border-info-100"
                  >
                    {{ tag }}
                  </BaseTag>
                </div>
              </div>
              <div v-if="audiences.length">
                <strong>{{ t('audience') }} </strong>
                <div class="mt-2 flex flex-wrap gap-2">
                  <BaseTag
                    v-for="tag in audiences.map((at) => at.name)"
                    :key="tag"
                    class="dark:!border-info-100"
                  >
                    {{ tag }}
                  </BaseTag>
                </div>
              </div>
              <div v-if="categories.length">
                <strong>{{ t('category') }}</strong>
                <div class="mt-2 flex flex-wrap gap-2">
                  <BaseTag
                    v-for="tag in categories.map((at) => at.name)"
                    :key="tag"
                    class="dark:!border-info-100"
                  >
                    {{ tag }}
                  </BaseTag>
                </div>
              </div>
            </div>
          </SidebarCard>

          <SidebarCard
            v-if="!!vacancy.locationOption || address"
            :title="t('section.location')"
          >
            <template v-if="address && address.latitude && address.longitude">
              <SimpleMap
                :initial-marker="{
                  longitude: address.longitude,
                  latitude: address.latitude,
                }"
                class="h-[300px]"
              />

              <div class="mt-4">
                <NuxtLink
                  class="hover:underline"
                  :to="googleMapsUrl"
                  target="_blank"
                >
                  {{ t('viewInGoogleMaps') }}
                </NuxtLink>
              </div>
            </template>

            <div v-else>
              <div v-if="vacancy.locationOption == LocationOption.Flexible">
                {{ t('locationOption.flexible') }}
              </div>
              <div v-else-if="vacancy && vacancy.locationOption == LocationOption.Home">
                {{ t('locationOption.home') }}
              </div>
              <div v-else>
                {{ t('locationOption.unknown') }}
              </div>
            </div>
          </SidebarCard>

          <!-- <VacancyMetaInfo v-if="canManageVacancy" :vacancy="vacancy" /> -->
        </div>
      </div>
      <!-- End sidebar column -->
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "pageTitle": "Vacancy",
    "aboutOrganization": "About {name}",
    "activityType": "Activity",
    "audience": "Audience",
    "category": "Category",
    "aboutThisOrganization": "About this organization",
    "when": "When",
    "locationOption": {
      "home": "You work from home",
      "flexible": "Location is flexible",
      "unknown": "Not specified"
    },
    "section": {
      "details": "Vacancy details",
      "location": "Location",
      "contactPerson": "Contact person"
    },
    "startDate": "Start date",
    "endDate": "End date",
    "date": "Date",
    "startTime": "Start time",
    "endTime": "End time",
    "requirements": "Requirements",
    "viewInGoogleMaps": "View in Google Maps"
  },
  "nl": {
    "pageTitle": "Vacature",
    "aboutOrganization": "Over {name}",
    "activityType": "Activiteit",
    "audience": "Doelgroep",
    "category": "Categorie",
    "aboutThisOrganization": "Over deze organisatie",
    "when": "Wanneer",
    "locationOption": {
      "home": "Je werkt vanuit huis",
      "flexible": "Locatie is flexibel",
      "unknown": "Niet opgegeven"
    },
    "section": {
      "details": "Vacature details",
      "location": "Locatie",
      "contactPerson": "Contactpersoon"
    },
    "startDate": "Begindatum",
    "endDate": "Einddatum",
    "date": "Datum",
    "startTime": "Starttijd",
    "endTime": "Eindtijd",
    "requirements": "Vereisten",
    "viewInGoogleMaps": "Bekijk op Google Maps"
  }
}
</i18n>
